import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend
} from "recharts";
import { Card, OverlayTrigger, Tooltip, Modal, Button } from "react-bootstrap"; 
import { FiInfo, FiX } from "react-icons/fi";

const GraficoOrdiniFornitori = () => {
  const [dataGrafico, setDataGrafico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((json) => {
        const ordiniRaw = json?.ORDINI_URGENTI?.flat() || [];

        const raggruppati = ordiniRaw.reduce((acc, curr) => {
          const nome = curr["Ragione sociale"] || "Sconosciuto";
          
          const parseImporto = (val) => {
            if (!val) return 0;
            return parseFloat(String(val).replace(/[^0-9.-]/g, "")) || 0;
          };

          const importoEva = parseImporto(curr["Importo Evaso"]);
          const importoDev = parseImporto(curr["Importo Da Evadere"]);
          const importoDevRim = parseImporto(curr["Importo Da Evadere rim"]);

          if (!acc[nome]) {
            acc[nome] = { evaso: 0, daEvadereStandard: 0, daEvadereRimesso: 0 };
          }

          acc[nome].evaso += importoEva;
          
          // Se "Importo Da Evadere rim" ha un valore valido (> 0), va nel BLU (rimesso)
          // Altrimenti, l'importo da evadere va nel GRIGIO (standard)
          if (importoDevRim > 0) {
            acc[nome].daEvadereRimesso += importoDev;
          } else {
            acc[nome].daEvadereStandard += importoDev;
          }

          return acc;
        }, {});

        // Formattazione e ordinamento REALE dal più grande al più piccolo
        const formattedData = Object.keys(raggruppati)
          .map((key) => {
            const evaso = parseFloat(raggruppati[key].evaso.toFixed(2));
            const daEvadereStandard = parseFloat(raggruppati[key].daEvadereStandard.toFixed(2));
            const daEvadereRimesso = parseFloat(raggruppati[key].daEvadereRimesso.toFixed(2));
            
            return {
              name: key,
              importoEvaso: evaso,
              importoDaEvadereStandard: daEvadereStandard,
              importoDaEvadereRimesso: daEvadereRimesso,
              // Il totale è dato dalla somma delle 3 componenti ora presenti sul grafico stacked
              totaleColonna: parseFloat((evaso + daEvadereStandard + daEvadereRimesso).toFixed(2)),
            };
          })
          .sort((a, b) => b.totaleColonna - a.totaleColonna)
          .slice(0, 10);

        setDataGrafico(formattedData);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore fetch grafico:", err);
        setLoading(false);
      });
  }, []);

  const formatXAxis = (tickItem) => {
    return tickItem.length > 12 ? `${tickItem.substring(0, 12)}...` : tickItem;
  };

  const renderTooltipSpiegazione = (props) => (
    <Tooltip id="info-grafico-tooltip" {...props} style={{ ...props.style, fontSize: '0.85rem' }}>
      Questo grafico mostra i top 10 fornitori ordinati per importo complessivo degli ordini urgenti.
      Ogni colonna divide l'importo Evaso (Rosso), l'importo Da Evadere standard (Grigio) e l'importo Da Evadere rimesso (Blu).
    </Tooltip>
  );

  const renderChartContent = (isFullscreen = false) => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={dataGrafico}
        margin={isFullscreen ? { top: 10, right: 30, left: 20, bottom: 60 } : { top: 0, right: 30, left: 10, bottom: 100 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
        
        <XAxis 
          dataKey="name" 
          tickFormatter={isFullscreen ? undefined : formatXAxis}
          interval={0} 
          angle={isFullscreen ? -25 : -40} 
          textAnchor="end"
          style={{ fontSize: isFullscreen ? '12px' : '11px', fontWeight: '500', fill: '#6c757d' }}
          dy={10}
        />
        
        <YAxis 
          tickFormatter={(value) => `€ ${value.toLocaleString("it-IT")}`} 
          style={{ fontSize: '11px', fill: '#6c757d' }}
          axisLine={false}
          tickLine={false}
        />
        
        <RechartsTooltip 
          cursor={{ fill: '#f8f9fa' }}
          formatter={(value, name) => {
            let label = "";
            if (name === "importoEvaso") label = "Importo Evaso";
            else if (name === "importoDaEvadereStandard") label = "Importo da Evadere";
            else if (name === "importoDaEvadereRimesso") label = "Importo da Evadere (Rimesso)";
            
            return [`€ ${value.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`, label];
          }}
          contentStyle={{ 
            borderRadius: '12px', 
            border: 'none', 
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            padding: '10px' 
          }}
        />

        <Legend 
          verticalAlign="top" 
          height={36} 
          formatter={(value) => {
            if (value === "importoEvaso") return "Importo Evaso";
            if (value === "importoDaEvadereStandard") return "Importo da Evadere";
            if (value === "importoDaEvadereRimesso") return "Importo da Evadere (Rimesso)";
            return value;
          }}
        />
        
        {/* Barra 1: EVASO (Rosso, base della colonna) */}
        <Bar 
          dataKey="importoEvaso" 
          stackId="a" 
          fill="#dc2626" 
          radius={0} 
          barSize={isFullscreen ? 50 : 35}
        />
        
        {/* Barra 2: DA EVADERE STANDARD (Grigio, centrale o testa a seconda dei casi) */}
        <Bar 
          dataKey="importoDaEvadereStandard" 
          stackId="a" 
          fill="#6c757d" 
          radius={0} 
          barSize={isFullscreen ? 50 : 35}
        />

        {/* Barra 3: DA EVADERE RIMESSO (Blu, ora in cima alla colonna con bordi arrotondati) */}
        <Bar 
          dataKey="importoDaEvadereRimesso" 
          stackId="a" 
          fill="#000000" // Blu moderno (Sky-600)
          radius={[6, 6, 0, 0]} // Arrotonda la testa della colonna finale
          barSize={isFullscreen ? 50 : 35}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  if (loading) return <div className="text-center p-5 text-muted">Caricamento grafico in corso...</div>;

  return (
    <Card className="border-0 shadow-lg overflow-hidden" style={{ borderRadius: '15px', height: '400px' }}>
      
      {/* HEADER DELLA CARD */}
      <div
        className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white"
        style={{ zIndex: 110 }}
      >
        <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer" }}>
          <span
            style={{
              fontWeight: "800",
              fontSize: "0.75rem",
              color: "#1a1a1a",
              letterSpacing: "1px",
              marginRight: "8px",
            }}
          >
            TOP 10 FORNITORI - DETTAGLIO FLUSSI E RIMESSI
          </span>
          
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 200, hide: 200 }}
            overlay={renderTooltipSpiegazione}
          >
            <span className="ms-2" onClick={(e) => e.stopPropagation()} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
              <FiInfo size={14} color="#adb5bd" />
            </span>
          </OverlayTrigger>
        </div>
      </div>
      
      {/* CORPO DELLA CARD */}
      <div className="p-2 flex-grow-1" style={{ height: '370px' }}>
        {renderChartContent(false)}
      </div>

      {/* MODALE SCHERMO INTERO */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 110 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                TOP 10 FORNITORI - VISTA DETTAGLIATA FLUSSI
              </span>
              <OverlayTrigger placement="bottom" overlay={renderTooltipSpiegazione}>
                <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
            </div>
            <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
              <FiX size={16} /> Chiudi
            </Button>
          </div>
          
          <div className="flex-grow-1 p-4 d-flex align-items-center justify-content-center" style={{ overflowY: "auto" }}>
            <div className="bg-white p-4 shadow-sm rounded-4 border w-100" style={{ maxWidth: "1200px", height: "550px" }}>
              {renderChartContent(true)}
            </div>
          </div>
        </Modal.Body>
      </Modal>

    </Card>
  );
};

export default GraficoOrdiniFornitori;